/** Speaks a reply. Resolves when speech finishes (or fails), so the talk loop can return to idle. */
export interface TtsAdapter {
  /** `sig` is the worker's signature for a reply it produced (lets the worker voice exactly that text). */
  speak(text: string, sig?: string): Promise<void>;
  cancel(): void;
}

export class SilentTts implements TtsAdapter {
  async speak(): Promise<void> {}
  cancel(): void {}
}

/** Free, built in: window.speechSynthesis. Prefers an en-GB male-ish voice. */
export class BrowserTts implements TtsAdapter {
  constructor(private readonly synth: SpeechSynthesis | undefined = globalThis.speechSynthesis, private readonly female = false) {}
  speak(text: string): Promise<void> {
    const s = this.synth;
    if (!s || typeof SpeechSynthesisUtterance === "undefined") return Promise.resolve();
    s.cancel();
    return new Promise((resolve) => {
      const u = new SpeechSynthesisUtterance(text);
      const voices = s.getVoices();
      u.voice = voices.find((v) => v.lang === "en-GB" && (this.female ? /serena|kate|susan|fiona|female|martha|stephanie/i : /daniel|george|male|arthur/i).test(v.name)) ?? voices.find((v) => v.lang === "en-GB") ?? null;
      u.lang = "en-GB";
      u.rate = 0.97;
      u.pitch = this.female ? 1.1 : 0.9;
      u.onend = u.onerror = () => resolve();
      s.speak(u);
      // Some browsers never fire onend when speech is blocked; cap by length.
      setTimeout(resolve, 1500 + text.length * 90);
    });
  }
  cancel(): void {
    this.synth?.cancel();
  }
}

/** OpenAI TTS / ElevenLabs through the worker's /tts. The worker picks the voice from `characterId`. Falls back to the browser voice. */
export class WorkerTts implements TtsAdapter {
  private audio: HTMLAudioElement | undefined;
  constructor(private readonly url: string, private readonly characterId: string, private readonly fallback: TtsAdapter = new BrowserTts()) {}
  async speak(text: string, sig?: string): Promise<void> {
    this.cancel();
    try {
      const res = await fetch(`${this.url}/tts`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ characterId: this.characterId, text, ...(sig ? { sig } : {}) }) });
      if (!res.ok) throw new Error(String(res.status));
      const src = URL.createObjectURL(await res.blob());
      const a = (this.audio = new Audio(src));
      await new Promise<void>((resolve) => {
        a.onended = a.onerror = () => resolve();
        a.play().catch(() => resolve());
      });
      URL.revokeObjectURL(src);
    } catch {
      await this.fallback.speak(text);
    }
  }
  cancel(): void {
    this.audio?.pause();
    this.audio = undefined;
    this.fallback.cancel();
  }
}
